import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const TYPE_LABELS = { normal: "standard (pay as you go)", on_account: "monthly", florist: "florist", partner: "partner", paint_shop: "paint shop" };

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (window.location.pathname === "/auth/callback") {
      setLoading(false);
      setUser(false);
      return;
    }
    const token = localStorage.getItem("kc_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => {
        localStorage.removeItem("kc_token");
        setUser(false);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password, turnstileToken = "") => {
    const { data } = await api.post("/auth/login", { email, password, cf_turnstile_response: turnstileToken || "" });
    localStorage.setItem("kc_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("kc_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("kc_token");
    if (window.caches) caches.delete("kilted-v2").catch(() => {});
    setUser(false);
  };

  const setSession = async (token, userObj) => {
    localStorage.setItem("kc_token", token);
    if (userObj) {
      setUser(userObj);
      return userObj;
    }
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  };

  // Re-sync the cached profile (admin may change customer_type mid-session)
  const refreshUser = async () => {
    if (!localStorage.getItem("kc_token")) return null;
    try {
      const { data } = await api.get("/auth/me");
      setUser((prev) => {
        if (!prev || JSON.stringify(prev) === JSON.stringify(data)) return prev || data;
        if (prev.customer_type !== data.customer_type) {
          const label = TYPE_LABELS[data.customer_type] || "standard";
          toast.success(`Good news — your account has been updated to a ${label} account. Everything's switched over for you, no need to log out.`, { duration: 9000 });
        }
        return data;
      });
      return data;
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem("kc_token"); setUser(false); }
      return null;
    }
  };

  // Silent background sync: every 60s and whenever the tab regains focus
  useEffect(() => {
    if (!user) return undefined;
    const tick = () => { if (document.visibilityState === "visible") refreshUser(); };
    const t = setInterval(tick, 60000);
    window.addEventListener("focus", tick);
    return () => { clearInterval(t); window.removeEventListener("focus", tick); };
  }, [!!user]); // eslint-disable-line react-hooks/exhaustive-deps

  const contextValue = useMemo(() => ({ user, loading, login, register, logout, setSession, refreshUser }),
    [user, loading]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { formatApiErrorDetail };
